import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { VirtualDataLocationDTO, VirtualLocationDTO } from '../../organisms/route-sheet/models/VirtualLocationDTO';
import { LocationCategoryDTO } from '../../../sections/locations-section/models/LocationCategoryDTO';
import { DataNamesDTO } from '../../../sections/locations-section/models/DataNamesDTO';
import { Subscription } from 'rxjs';
import { LocationCategoriesService } from '../../../sections/locations-section/services/LocationCategories.service';
import { VirtualLocationsService } from '../../../sections/locations-section/services/VirtualLocations.service';
import { EnumLocationCategories } from 'src/app/features/shared/helpers/enums/EnumLocationCategories';
import { NotificationsToastService } from 'src/app/features/shared/services/Notifications-Toast.service';
import { MapHelperService } from 'src/services/map-helper.service';
import mapboxgl from 'mapbox-gl';

@Component({
  selector: 'app-popup-create-virtual-location',
  templateUrl: './popup-create-virtual-location.component.html',
  styleUrls: ['./popup-create-virtual-location.component.css']
})
export class PopupCreateVirtualLocationComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('scrollContainer') scrollContainer: ElementRef<HTMLElement>;

  category: LocationCategoryDTO = {} as LocationCategoryDTO;

  dataFields: VirtualDataLocationDTO[] = [];

  selectedTabIndex: number = 0;

  keyNames: DataNamesDTO[] = [];
  statusOptions: { label: string; value: number }[] = [];

  originalDataLength: number = 0;

  detailsForm: FormGroup = this.formBuilder.group({
    identifier: ['', Validators.required],
    cordsX: ['', Validators.required],
    cordsY: ['', Validators.required],
    status: [null],
    dataValues: this.formBuilder.array([])
  });

  isLoading: boolean = true;
  fullScreen: boolean = false;
  mobileQuery: MediaQueryList = { matches: false } as MediaQueryList;

  groups: { idGroup: number, idSubGroup: number } = { idGroup: 0, idSubGroup: 0 };

  isSelectingLocation: boolean = false;
  mapClickHandler: any = null;
  locationMarker: mapboxgl.Marker = null;

  subscription_GetStatuses: Subscription;
  subscription_CreateLocation: Subscription;

  constructor(
    public dialogRef: MatDialogRef<PopupCreateVirtualLocationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private locationCategoriesService: LocationCategoriesService,
    private virtualLocationsService: VirtualLocationsService,
    private notificationsToastService: NotificationsToastService,
    private mapHelperService: MapHelperService
  ) {

  }

  ngOnInit() {
    this.category = this.data?.category ?? this.category;
    this.keyNames = this.data?.keyNames ?? [];
    this.mobileQuery = this.data?.mobileQuery ?? this.mobileQuery;
    this.groups = this.data?.groups ?? this.groups;

    //solo se usan los keys de la plantilla; los value siempre inician vacíos
    const templateKeys: string[] = this.data?.keys ?? [];
    this.dataFields = templateKeys.map((key, index): VirtualDataLocationDTO => ({
      idOrder: index,
      key: key,
      value: '',
      dataType: 'string'
    }));

    this.detailsForm = this.formBuilder.group({
      identifier: ['', Validators.required],
      cordsX: ['', Validators.required],
      cordsY: ['', Validators.required],
      status: [null],
      dataValues: this.formBuilder.array(this.dataFields.map((item) => this.buildDataGroup(item))),
    });
    this.originalDataLength = this.dataFields.length;

    this.getLocationStatuses();
    this.isLoading = false;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.scrollContainer) {
        this.scrollContainer.nativeElement.scrollTop = 0;
      }
    });
  }

  get dataValuesArray(): FormArray {
    return this.detailsForm.get('dataValues') as FormArray;
  }

  buildDataGroup(dataField: VirtualDataLocationDTO): FormGroup {
    return this.formBuilder.group({
      key: [dataField.key],
      value: [dataField.value],
      dataType: [dataField.dataType],
      idOrder: [dataField.idOrder]
    });
  }

  isNewDataField(index: number): boolean {
    return index >= this.originalDataLength;
  }

  addNewDataField() {
    const newDataField: VirtualDataLocationDTO = {
      idOrder: this.dataFields.length,
      key: '',
      value: '',
      dataType: 'string'
    };
    this.dataFields.push(newDataField);
    this.dataValuesArray.push(this.buildDataGroup(newDataField));
  }

  removeDataField(index: number) {
    this.dataFields.splice(index, 1);
    this.dataValuesArray.removeAt(index);
  }

  isDuplicateKey(index: number): boolean {
    const key = (this.dataValuesArray.at(index).get('key').value || '').trim().toLowerCase();
    if (!key) return false;
    return this.dataValuesArray.controls.some((control, i) =>
      i !== index && (control.get('key').value || '').trim().toLowerCase() === key
    );
  }

  hasDuplicateKeys(): boolean {
    return this.dataValuesArray.controls.some((_, i) => this.isDuplicateKey(i));
  }

  isEmptyKey(index: number): boolean {
    return this.isNewDataField(index) && !(this.dataValuesArray.at(index).get('key').value || '').trim();
  }

  hasEmptyNewKeys(): boolean {
    return this.dataValuesArray.controls.some((data, i) => this.isEmptyKey(i));
  }

  ngOnDestroy(): void {
    if (this.subscription_GetStatuses) {
      this.subscription_GetStatuses.unsubscribe();
    }
    if (this.subscription_CreateLocation) {
      this.subscription_CreateLocation.unsubscribe();
    }
    this.stopSelecting();
    this.removeLocationMarker();
  }

  
  getLocationStatuses(){
    this.subscription_GetStatuses = this.locationCategoriesService.getCategoryStatusList(this.category.id).subscribe({
      next: (data) => {
        if(data && data.isSuccess){
          const statuses = data.result;
          this.statusOptions = statuses.map((status) => ({
            label: status.locationStatusValue,
            value: status.id
          }));
          if (this.statusOptions.length > 0) {
            this.detailsForm.get('status')?.setValidators([Validators.required]);
          } else {
            this.detailsForm.get('status')?.clearValidators();
          }
          this.detailsForm.get('status')?.updateValueAndValidity();
        }
      },
      error: (error) => {
        console.error('Error al obtener los estatus de categoría', error);
      }
    });
  }

  getUsableName(keyName: string): string {
    const keyNameObject = this.keyNames.find((item) => item.keyName == keyName);
    return keyNameObject ? keyNameObject.usableName : keyName;
  }

  //Obtiene la visibilidad según el nombre de la key
  getNameVisibility(keyName) {
    const keyNameObject = this.keyNames.find(
      (item) => item.keyName === keyName
    );
    return keyNameObject ? keyNameObject.isVisible : true;
  }

  private getSelectedStatusId(): number {
    const value = this.detailsForm.get('status')?.value;
    return value === null || value === undefined || value === '' ? null : Number(value);
  }

  CancelButton(){
    this.stopSelecting();
    this.removeLocationMarker();
    this.dialogRef.close();
  }

  buildRequestData(): VirtualDataLocationDTO[] {
    const dataValues: { key: string; value: string }[] = this.dataValuesArray.value;
    return this.dataFields.map((item, index) => ({
      ...item,
      key: dataValues[index]?.key || item.key,
      value: dataValues[index]?.value ?? item.value
    }));
  }

  CreateButton() {
    if (this.isLoading)
      return;
    this.detailsForm.markAllAsTouched();
    if (this.hasDuplicateKeys() || this.hasEmptyNewKeys())
      return;
    if (this.detailsForm.get('identifier')?.invalid || this.detailsForm.get('cordsX')?.invalid || this.detailsForm.get('cordsY')?.invalid || this.detailsForm.get('status')?.invalid)
      return;

    this.isLoading = true;
    const requestLocationDTO: VirtualLocationDTO = {
      id: 0,
      cords: null,
      idGroup: this.groups.idGroup,
      idSubGroup: this.groups.idSubGroup,
      idLocationCategory: this.category.id,
      guid: null,
      cordsX: this.detailsForm.get('cordsX')?.value ?? '',
      cordsY: this.detailsForm.get('cordsY')?.value ?? '',
      identifier: this.detailsForm.get('identifier')?.value ?? '',
      description: '',
      locationCategory: this.category.name,
      icon: this.category.icon,
      activeIcon: this.category.activeIcon,
      status: 1,
      data: this.buildRequestData(),
      idLocationCategoryStatus: this.getSelectedStatusId()
    };

    this.subscription_CreateLocation = this.virtualLocationsService.createOrUpdateVirtualLocation(requestLocationDTO).subscribe({
      next: (data) => {
        if(data && data.isSuccess){
          this.removeLocationMarker();
          this.dialogRef.close({
            createdLocation: {
              ...requestLocationDTO
            },
            isCreated: true
          });
        } else {
          this.notificationsToastService.showError('No se pudo crear la ubicación', 'Error al crear');
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error al crear la ubicación virtual', error);
        this.notificationsToastService.showError('No se pudo crear la ubicación', 'Error al crear');
        this.isLoading = false;
      }
    });
  }


  //#region ------ selección de coordenadas en el mapa ------

  selectOnMap(): void {
    if (this.isSelectingLocation || !this.mapHelperService._map) {
      return;
    }
    this.isSelectingLocation = true;
    this.mapHelperService.isPointPickingActive = true;
    this.mapHelperService._map.getCanvas().style.cursor = 'crosshair';

    this.mapClickHandler = (event: any) => {
      const { lng, lat } = event.lngLat;
      this.setCoords(lng, lat);
      this.stopSelecting();
    };
    this.mapHelperService.addEventOnMap('click', this.mapClickHandler);
  }

  setCoords(lng: number, lat: number): void {
    this.detailsForm.get('cordsX')?.setValue(lng.toFixed(6));
    this.detailsForm.get('cordsY')?.setValue(lat.toFixed(6));
    this.detailsForm.get('cordsX')?.markAsTouched();
    this.detailsForm.get('cordsY')?.markAsTouched();

    this.removeLocationMarker();
    this.locationMarker = new mapboxgl.Marker({ color: '#3073ca', draggable: true })
      .setLngLat([lng, lat])
      .addTo(this.mapHelperService._map);

    this.locationMarker.on('dragend', () => {
      const dragged = this.locationMarker.getLngLat();
      this.detailsForm.get('cordsX')?.setValue(dragged.lng.toFixed(6));
      this.detailsForm.get('cordsY')?.setValue(dragged.lat.toFixed(6));
    });
  }

  removeLocationMarker(): void {
    if (this.locationMarker) {
      this.locationMarker.remove();
      this.locationMarker = null;
    }
  }

  stopSelecting(): void {
    if (this.mapClickHandler) {
      this.mapHelperService.RemoveEventFromMap('click', this.mapClickHandler);
      this.mapClickHandler = null;
    }
    this.isSelectingLocation = false;
    this.mapHelperService.isPointPickingActive = false;
    if (this.mapHelperService._map) {
      this.mapHelperService._map.getCanvas().style.cursor = '';
    }
  }

  //#endregion

}
